// P2-INSTALL — **ADD 12TH HOUSE TO YOUR HOME SCREEN.** The missing first hop of the funnel.
//
// **TINA'S RECEIPT IS WHY THIS IS URGENT RATHER THAN NICE.** Her ask-check row reads:
//
//   reasons: ["this browser cannot do web push", "not the installed app"]
//   installed: false, supported: false, standaloneMedia: false, navigatorStandalone: false
//
// She added the app to her home screen and then opened the SITE, in a browser tab. The ask refused
// correctly — on iOS web push exists ONLY inside the installed app, so there was nothing to offer
// her — and nothing anywhere told her the difference. **Every one of the 300 walks that same path**,
// and the ask can never rescue them, because the ask lives on the far side of the door this prompt
// is the handle for.
//
// Pure, importing nothing, so `scripts/pi1-install-prompt.mjs` RUNS it.

export const INSTALL_ANSWERS = { INSTALLED: "installed", LATER: "later", DISMISSED: "dismissed" };
export const INSTALL_ANSWER_VALUES = Object.values(INSTALL_ANSWERS);
export const installAnswerValid = (a) => INSTALL_ANSWER_VALUES.includes(String(a || ""));

// **THE TWO PLATFORMS NEED TWO DIFFERENT PROMPTS, and that is the whole reason this is not one
// button.** Android/Chrome hands the page a real `beforeinstallprompt` event and can install with a
// tap. iOS hands the page NOTHING: there is no API, and the only route is Share -> Add to Home
// Screen, which a member cannot be expected to know. So iOS gets TEACHING and Android gets a BUTTON,
// and offering a dead button to an iPhone would be the worse half of guessing.
export const PLATFORM = { IOS: "ios", ANDROID: "android", OTHER: "other" };

export function platformOf(win) {
  const ua = String(win?.navigator?.userAgent || "");
  // iPadOS reports as a Mac, so touch points are what separate a tablet from a desktop.
  const iPadish = /Macintosh/.test(ua) && Number(win?.navigator?.maxTouchPoints || 0) > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadish) return PLATFORM.IOS;
  if (/Android/.test(ua)) return PLATFORM.ANDROID;
  return PLATFORM.OTHER;
}

export function isStandalone(win) {
  if (!win) return false;
  try {
    if (win.navigator && win.navigator.standalone === true) return true;
    if (typeof win.matchMedia === "function" && win.matchMedia("(display-mode: standalone)").matches) return true;
  } catch { /* a partial window is not the installed app */ }
  return false;
}

// ---------------------------------------------------------------------------
// WHEN IT OFFERS. Same discipline as the push ask, and the reasons are RETURNED rather than
// discarded — which is the lesson P3-2B paid for: a prompt that refuses silently is three bugs
// wearing one face.
// ---------------------------------------------------------------------------
// >>> INSTALL-1, HER CLAUSE 5 — **WHAT COUNTS AS A DESKTOP.** <<<
//
// One declaration, because `shouldOfferInstall` and `shouldOfferPush` must never disagree about it:
// the first refuses a desktop a home-screen sheet it has no route for, and the second now OFFERS a
// desktop the notification permission its browser was always ready to grant. Two spellings of *is
// this a desktop* is one member refused by both (R28/R29).
//
// **IT IS A POSITIVE FINDING, NEVER `!mobile` (R19/R8-4).** A window this cannot read answers FALSE,
// which leaves that member exactly today's behaviour rather than handing an unknown context the
// widened path.
export function isDesktop(win) {
  if (!win) return false;
  if (platformOf(win) !== PLATFORM.OTHER) return false;
  const width = Number(win?.innerWidth);
  if (!Number.isFinite(width) || width <= 0) return false;
  // A touch-first device reporting as OTHER is not a desktop, whatever its width.
  if (Number(win?.navigator?.maxTouchPoints || 0) > 1) return false;
  return width > 900;
}

// ---------------------------------------------------------------------------
// INSTALL-1, HER CLAUSE 4 — **POLITE PERSISTENCE ON *NOT NOW*.**
//
// **WHAT IT REPLACES, MEASURED: *not now* WAS PERMANENT.** `install_answer = 'later'` made
// `shouldOfferInstall` refuse with *already answered: later* forever, so one tap on the softest
// control in the popup ended the funnel's first hop for that member for good — and on an iPhone the
// installed app is the ONLY place web push exists, so it ended notifications with it.
//
// **THREE ANSWERS, AND ONLY ONE OF THEM IS FOREVER (R18).**
//   `installed` — she did it. NEVER ask again, absolutely: telling a member to install the thing she
//                 is standing in is the most confusing thing this prompt could do.
//   `later`     — *not now* is not *never*. She is asked again after a COOL-OFF, a BOUNDED number of
//                 times, and then never again.
//   `dismissed` — treated as `later`. A member who closed the sheet without choosing said less than
//                 one who tapped *Not now*, so reading it as the harder refusal is the direction that
//                 costs her the funnel (R19).
//
// **THE CAP IS WHAT MAKES IT POLITE RATHER THAN A NAG WITH A TIMER ON IT.** A cool-off with no
// ceiling re-offers forever, which is what P2's ONCE law forbids one surface over. Both numbers are
// DECLARED so moving either is one edit, and both are HERS — this round picked the conservative pair
// under the autonomous law and logged it (DECISIONS INSTALL-1-3).
//
// **AN UNREADABLE COUNT OR STAMP REFUSES, AND THE DIRECTION IS DELIBERATE (R19/R53/R136/R137).**
// `Number(true)` is 1 and `Number("")` is 0, so a coerced count would re-offer a member who has
// already been asked her three times. Over-asking is the harm this clause exists to bound, so
// anything that is not a real finite number reads as *she has been asked enough*.
export const INSTALL_ASK_CAP = 3;
export const INSTALL_COOLOFF_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

export function installReofferState({ answer = null, answeredAt = null, asks = 0, nowMs = 0 } = {}) {
  const a = String(answer || "");
  if (!a) return { mayOffer: true, why: "never answered" };
  if (!installAnswerValid(a)) return { mayOffer: true, why: "unknown answer, read as never answered" };
  if (a === INSTALL_ANSWERS.INSTALLED) return { mayOffer: false, why: "already installed" };

  const count = typeof asks === "number" && Number.isFinite(asks) ? asks : Number.POSITIVE_INFINITY;
  if (count >= INSTALL_ASK_CAP) return { mayOffer: false, why: `asked ${INSTALL_ASK_CAP} times already` };

  const stamp = answeredAt instanceof Date ? answeredAt.getTime() : Date.parse(String(answeredAt || ""));
  const now = typeof nowMs === "number" && Number.isFinite(nowMs) ? nowMs : NaN;
  if (!Number.isFinite(stamp) || !Number.isFinite(now)) {
    return { mayOffer: false, why: "cannot read when she was last asked" };
  }
  const waited = now - stamp;
  if (waited < INSTALL_COOLOFF_DAYS * DAY_MS) {
    return { mayOffer: false, why: `answered ${a} less than ${INSTALL_COOLOFF_DAYS} days ago` };
  }
  return { mayOffer: true, why: `answered ${a}, cool-off served` };
}

// ===============================================================================================
// INSTALL-1 CLAUSE 7 — **THE WAY BACK, AND IT WAS THE ONE HALF OF THIS FEATURE WIRED TO NOTHING.**
//
// `server/push-routes.js` and `InstallPrompt.tsx` have both PROMISED *"re-offerable from My Room
// settings"* since the day they were written, and R125 measured that `/api/install/reset` was
// called by no client file and `forceOpen` passed by no call site — R165's shape exactly: **a
// capability the house has, says it has, and does not.** F7-3 parked it because *where the control
// goes and what it says in both languages is hers*.
//
// >>> HER MENU SIZE ORDER ANSWERED THE WHERE, BY NAME. <<< It lists the entries that must be kept
// and fitted in the member menu: *"Have an issue?, SMS Notifications, **the re-offer button**"* —
// so it goes where `PushSettings` and `SmsSettings` already live, which is also the surface that
// is on EVERY page rather than only inside My Room (their own recorded reason).
//
// **THREE STATES, NEVER TWO (R18/L5.1), AND THE FIRST ONE IS WHAT A NAIVE BUILD LOSES.** *We have
// not looked yet* is not *she has not installed it*: read as the second, this control flashes an
// offer at a member standing in the installed app on every single open of her own menu.
// ===============================================================================================

export const INSTALL_REOFFER_STATES = Object.freeze(["unknown", "hidden", "installed", "offer"]);

/**
 * What the menu control should be. `known` is *the install state has ANSWERED* — the same
 * distinction `membershipKnown` is built around one surface over, and for the same reason.
 *
 * **`standalone` OUTRANKS EVERYTHING**, because telling a member to add the thing she is already
 * standing in is what `shouldOfferInstall` suppresses absolutely, and a menu entry offering to
 * ask her again is the same sentence one control down.
 *
 * >>> AND `desktop` OUTRANKS EVEN THAT, WHICH IS HER ART-DIRECTION PASS CLAUSE 5: *"render MOBILE
 * ONLY — desktop members never see it."* <<<
 *
 * **`hidden` IS ITS OWN STATE RATHER THAN A SECOND `unknown` (R18).** They draw the same nothing
 * and they are not the same fact: `unknown` is *we have not looked yet* and becomes an offer the
 * moment the read lands, while `hidden` is *this device has no home screen to add anything to* and
 * can never become one. Collapsed, a later round reading "unknown" as a waiting state would flash
 * the control at a desktop member the instant her install state answered.
 *
 * **AND `desktop` DEFAULTS TO FALSE, WHICH IS THE SAFE DIRECTION (R19/ITEM 12's neutrality
 * argument).** A caller who forgets it shows the control where she does not want it — a cosmetic
 * fault she can see and report. Defaulting the other way would HIDE it on a phone, which costs a
 * member the only route to the installed app, and on iOS the only route to notifications at all.
 * The wiring is held by its own clause rather than by this default.
 */
export function installReofferControl(input) {
  // A law that THROWS is worse than one that answers: `= {}` only defaults on `undefined`, so a
  // caller handing over `null` crashed the menu rather than drawing nothing (R19).
  const { standalone = false, known = false, answer = null, desktop = false } = input && typeof input === "object" ? input : {};
  // R53/R136/R137 here too: this arrives from a user-agent read, and a coerced value would hide a
  // phone member's only way into the installed app.
  if (desktop === true) return "hidden";
  if (standalone === true) return "installed";
  // R53/R136/R137: presence, never truthiness. `known` arrives from a fetch that may not have
  // landed, and a coerced value here draws an offer over an unanswered read.
  if (known !== true) return "unknown";
  if (String(answer || "") === INSTALL_ANSWERS.INSTALLED) return "installed";
  return "offer";
}

/** >>> THE RE-OFFER IS ANNOUNCED, BECAUSE THE SHEET'S OPEN STATE IS ITS OWN AND THIS MENU IS NOT
 *  IN ITS TREE. <<< `CauldronDoor`'s own shape and `OPEN_SITE_MENU_EVENT`'s: lifting the flag into
 *  App.tsx to thread it back down would put the decision inside an eleven-thousand-line file no
 *  gate can reach (R8/R95). One declaration, so a second caller cannot invent a second door. */
export const INSTALL_REOFFER_EVENT = "twelve:install-reoffer";

export function askToInstallAgain(win = window) {
  win.dispatchEvent(new CustomEvent(INSTALL_REOFFER_EVENT));
}
export function shouldOfferInstall({
  standalone = false, signedIn = false, hasRoom = false, priorAnswer = null,
  platform = PLATFORM.OTHER, mobile = true, answeredAt = null, asks = 0, nowMs = 0,
} = {}) {
  const reasons = [];
  // **SUPPRESSED INSIDE THE INSTALLED APP**, absolutely: telling a member to install the thing she is
  // already standing in is the single most confusing thing this prompt could do.
  if (standalone) reasons.push("already installed");
  if (!signedIn) reasons.push("not signed in");
  // Her spec: a member who reaches HER ROOM. Before that she has nothing to come back to.
  if (!hasRoom) reasons.push("no room yet");
  // HER CLAUSE 4. The prior answer is no longer a wall — it is a question for the re-offer law, and
  // the law's own `why` is what lands on the receipt so P3-2B's reporting rule still holds.
  const reoffer = installReofferState({ answer: priorAnswer, answeredAt, asks, nowMs });
  if (!reoffer.mayOffer) reasons.push(reoffer.why);
  // A desktop home screen is not what this is for, and the iOS teaching would be nonsense there.
  // **HER CLAUSE 5 DOES NOT CHANGE THIS**: desktop gets NOTIFICATIONS, which need no install at all
  // (`shouldOfferPush`'s own `desktop` clause), never a home-screen sheet it has no route for.
  if (!mobile && platform === PLATFORM.OTHER) reasons.push("desktop browser");
  return { offer: reasons.length === 0, reasons };
}

// ---------------------------------------------------------------------------
// THE WORDS — **INSTALL-1's CLAUSES 1 AND 6 (her word, 2026-08-17).**
//
// The retired `INSTALL_COPY` was RAW ENGLISH and its own comment flagged it as a placeholder
// awaiting her word. This is her word, and it moved two ways at once:
//
//   **CLAUSE 1, THE ONE-LINE ASK.** A title, a two-clause body, a lead, a why-line and a step list
//   all met a member at once on a phone. It is ONE LINE now — what it is and why, in a sentence —
//   with the platform route underneath it. The `whyItMatters` paragraph is not deleted, it is the
//   second half of that sentence, because the reason IS the persuasion.
//
//   **CLAUSE 6, BOTH LANGUAGES.** The copy is `strings.js` rows now, which is where the SERVER can
//   compose from it too and where ITEM 14b's census can hold both halves. This module keeps only the
//   KEYS, because it imports NOTHING — the entry requirement that lets `pi1-install-prompt.mjs`
//   LOAD and RUN it — and importing `t` here would buy the translation and pay for it with the
//   property the whole file exists for (`numerology-stages.ts`'s own ruling, R211).
// ---------------------------------------------------------------------------
export const INSTALL_KEYS = {
  ask: "install.ask",
  action: "install.action",
  later: "install.later",
  // **ANDROID'S OWN TEACHING, AND IT EXISTS BECAUSE THE REAL PROMPT IS NOT ALWAYS THERE.** Chrome
  // fires `beforeinstallprompt` only when IT decides the site qualifies, and never at all in a
  // Samsung Internet / Firefox / in-app webview. Without these steps that member met a button that
  // silently did nothing — see `installPlan`. Safari's Share sheet is not on an Android phone, so
  // handing her the iOS steps would be teaching her to look for a control that is not on her screen.
  iosSteps: ["install.iosStep1", "install.iosStep2"],
  androidSteps: ["install.androidStep1", "install.androidStep2"],
};

// ---------------------------------------------------------------------------
// **THE OFFER, DECIDED AND REPORTED IN ONE PLACE.** This was the body of an effect inside the
// component, which is why P3-2B's law — the reasons are REPORTED, refusals included — could only ever
// be held as a POSITION in a TSX file: the `/api/install/seen` post sits ABOVE the `!offer` return,
// and nothing but a browser could tell. `readState` and `report` are PARAMETERS (R46), so a gate
// RUNS it: every refusal reason really lands, and a refused offer still reports.
//
// **AN UNMOUNTED PROMPT REPORTS NOTHING AND OPENS NOTHING**, which is why `alive` is asked here and
// not only by the caller — the state read is a round trip and she can leave the room inside it.
//
// **A FAILED STATE READ IS THE OFFERING CASE, DELIBERATELY.** `push-routes.js` already names this in
// its own words: a failed state fetch "reads as never answered, which is the offering case". Offering
// twice costs a member one tap; refusing on an unreadable answer costs the whole first hop of the
// funnel, which is what this prompt exists to be.
// ---------------------------------------------------------------------------
export async function runInstallOffer({
  win, hasRoom = false, readState, report, alive = () => true, promptReady = false,
  // INSTALL-1 clause 4: the clock is a PARAMETER, so a gate drives the cool-off across its boundary
  // without waiting fourteen days and without a fake global (R46's `doFetch` pattern).
  nowMs = Date.now(),
} = {}) {
  const state = await readState();
  if (!alive()) return { offer: false, reasons: ["gone"], platform: PLATFORM.OTHER, reported: false };
  const platform = platformOf(win);
  const decision = shouldOfferInstall({
    standalone: isStandalone(win),
    signedIn: true,
    hasRoom,
    priorAnswer: state?.answer || null,
    answeredAt: state?.answeredAt || null,
    asks: Number(state?.asks) || 0,
    nowMs,
    platform,
    mobile: Number(win?.innerWidth || 0) <= 900 || platform !== PLATFORM.OTHER,
  });
  // **`promptReady` RIDES THE RECEIPT BECAUSE THE CENSUS COULD NOT SEE IT, AND IT IS THE ONE FACT
  // F7's TRACE WANTED.** The decision log records the PLATFORM and recorded nothing about whether
  // Android's real prompt was available — so *"an Android member was offered"* and *"an Android
  // member was offered something that works"* were one row (R18). Now they are two.
  await report({ offered: decision.offer, reasons: decision.reasons, platform, promptReady: promptReady === true });
  return { offer: decision.offer, reasons: decision.reasons, platform, reported: true };
}

// What the prompt SHOWS, per platform. One function, so the component never branches on the platform
// itself and a gate can ask for every platform's shape without a browser.
//
// >>> **`promptReady` IS THE SECOND ARGUMENT BECAUSE ANDROID'S BUTTON IS ONLY REAL WHEN THE BROWSER
// HAS HANDED US THE EVENT, AND MEASURING F7 FOUND IT WAS OFFERED WHETHER IT HAD OR NOT.** <<< Chrome
// fires `beforeinstallprompt` only when it decides the site qualifies, and Samsung Internet, Firefox
// and every in-app webview never fire it at all. Without the event the tap could do nothing whatever
// — and the component then closed the prompt and stamped `later` on her account, so **a member who
// tapped ADD IT was recorded as having declined and could never be offered again.** Her F7 words are
// *"Android the REAL prompt"*; a button that cannot prompt is not one.
//
// **THIS IS THIS FILE'S OWN STATED LAW APPLIED TO THE CASE IT DID NOT ASK ABOUT** — the unknown
// platform is already taught for exactly this reason, in as many words, three lines down.
//
// **IT DEFAULTS TO FALSE, AND THE DIRECTION IS THE RULING (R19/R8-4):** teaching a member who could
// have had one tap costs her two taps and an instruction she can follow; buttoning a member whose
// browser has no prompt costs her the install AND permanently suppresses the offer, with no symptom
// at all.
// It answers KEYS rather than sentences (INSTALL-1 clause 6), so the caller resolves them in HER
// language and this module still imports nothing.
export function installPlan(platform, promptReady = false) {
  if (platform === PLATFORM.ANDROID && promptReady === true) {
    return { kind: "prompt", action: INSTALL_KEYS.action, steps: [] };
  }
  if (platform === PLATFORM.ANDROID) {
    return { kind: "teach", action: "", steps: [...INSTALL_KEYS.androidSteps] };
  }
  if (platform === PLATFORM.IOS) {
    return { kind: "teach", action: "", steps: [...INSTALL_KEYS.iosSteps] };
  }
  // An unknown platform is TAUGHT rather than handed a button that may do nothing. A dead button is
  // worse than an instruction, because she taps it and concludes the app is broken.
  return { kind: "teach", action: "", steps: [...INSTALL_KEYS.iosSteps] };
}

// ---------------------------------------------------------------------------
// ITEM 7(a), HER SECOND HALF — **INSTALL, THEN NOTIFICATIONS, ONE WARM MOTION.**
//
// Her words: *"the install prompt fixed, Android real prompt and iOS instructional card, WITH THE
// NOTIFICATION-PERMISSION ASK FLOWING DIRECTLY AFTER THE INSTALL — one warm motion."*
//
// >>> MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE GAP IS REAL BUT IT IS ONLY HALF THE
// SIZE IT LOOKS. <<< W13-1b's ask already appears automatically on the first signed-in open of the
// INSTALLED app, so on iOS — where web push exists ONLY inside that app — *directly after the
// install* is structurally the next launch and is already built. **On Android it is not:** the
// browser prompt installs the app while she is still standing in the TAB, `isInstalledApp(window)`
// is false there, and `shouldOfferPush` refuses with *"not the installed app"* — so the warm motion
// ends at the install and the ask arrives on some later launch, which is not one motion at all.
//
// >>> THREE OUTCOMES, NEVER TWO, AND THE THIRD IS THE ONE THAT COSTS SOMETHING (R18). <<<
//
//   * `ask`       — she really installed AND this context can do web push: the ask follows NOW.
//   * `wait`      — she really installed and this context CANNOT (iOS, and any browser with no
//                   PushManager). The ask is still owed and it lands on the first open of the
//                   installed app, which is exactly where it already lives. *Not now* is not *never*.
//   * `none`      — she did not install. **Nothing flows.**
//
// **`none` IS THE RULING, AND CHAINING THE ASK ONTO THE CLOSE HANDLER REGARDLESS OF THE ANSWER IS
// THE VERSION ANYBODY WOULD SHIP.** A member who just said *not now* to the home screen is the
// coldest possible audience for a permission prompt — and W13-1b's own opening law is that a DENIED
// permission is PERMANENT AND UNRECOVERABLE IN-APP, so spending her one irreversible chance on the
// back of a refusal is the actual risk here. Her sentence is *after the INSTALL*, not *after the
// prompt closes*.
export const INSTALL_HANDOFF = { ASK: "ask", WAIT: "wait", NONE: "none" };

export function installHandoff({ answer = "", pushHere = false } = {}) {
  // Presence, never truthiness (R53/R136/R137): this value crosses a component boundary and
  // `Boolean("false")` is `true`. Only the one answer that means SHE INSTALLED IT hands off.
  if (String(answer || "") !== INSTALL_ANSWERS.INSTALLED) return INSTALL_HANDOFF.NONE;
  return pushHere === true ? INSTALL_HANDOFF.ASK : INSTALL_HANDOFF.WAIT;
}

// ---------------------------------------------------------------------------
// **THE CARRIER, AND IT IS AN EVENT BECAUSE THE TWO ENDS ARE SIBLINGS.**
//
// The prompt and the ask are two components mounted side by side in App.tsx, and App.tsx is at its
// R8 ceiling — so lifting a flag up into it to pass back down would cost lines AND would put the one
// wiring that decides whether her warm motion happens inside a 11,500-line file where no gate can
// reach it (R8/R95). `CauldronDoor`'s own shape, one door over: the event is DECLARED in the law,
// and `win` is a PARAMETER so a gate RUNS both ends against a fake window rather than needing a live
// Chrome (R26's own repair, R46's `doFetch` pattern).
export const INSTALLED_EVENT = "12h:installed";

export function announceInstalled(win) {
  // A window that cannot dispatch is a window that cannot hurt anybody: the install still happened
  // and the ask still arrives on the first open of the installed app. It must never THROW, because
  // this is called from inside the close handler of a prompt she has just used successfully.
  try {
    if (!win || typeof win.dispatchEvent !== "function") return false;
    const Ev = win.CustomEvent || win.Event;
    if (typeof Ev !== "function") return false;
    win.dispatchEvent(new Ev(INSTALLED_EVENT));
    return true;
  } catch { return false; }
}

// Returns the UNSUBSCRIBE, so a component's effect cleanup is one line and a listener cannot outlive
// the mount that added it.
export function watchInstalled(win, fn) {
  if (!win || typeof win.addEventListener !== "function" || typeof fn !== "function") return () => {};
  win.addEventListener(INSTALLED_EVENT, fn);
  return () => { try { win.removeEventListener(INSTALLED_EVENT, fn); } catch { /* already gone */ } };
}
